import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Layers as LayersIcon,
  Image as ImageIcon,
  Diamond as DiamondIcon,
  Code as CodeIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';

interface TutorialPageProps {
  onBackToHome: () => void;
}

const TutorialPage: React.FC<TutorialPageProps> = ({ onBackToHome }) => {
  const theme = useTheme();

  const tutorialSteps = [
    {
      label: 'Collection Configuration',
      description: 'Set up the basic parameters for your NFT collection.',
      icon: <SettingsIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            The first step is to configure the basic parameters for your NFT collection. This includes:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Collection Name Prefix" 
                secondary="Choose a unique and memorable prefix for your collection (e.g., 'MyCollection #1')."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Description" 
                secondary="Write a compelling description that explains your collection's concept."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Base URI (Optional)" 
                secondary="Add an IPFS or other URI prefix for your metadata image links. This will be used in the metadata JSON as 'image': 'ipfs://baseURI/#.png'."
              />
            </ListItem>
            <Box sx={{ ml: 4, my: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.08)', borderRadius: 2, border: '1px solid rgba(98, 0, 234, 0.2)' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                More about Base URI
              </Typography>
              <Typography variant="body2" paragraph>
                The Base URI is used to create proper IPFS links in your metadata files. For example, if your Base URI is "QmYourCID", the metadata will include image links like "ipfs://QmYourCID/1.png".
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>When to use it:</strong> Add a Base URI if you plan to upload your collection images to IPFS or another decentralized storage solution. This ensures your NFT metadata points to the correct location.
              </Typography>
              <Typography variant="body2">
                <strong>Note:</strong> This field is optional and only affects the metadata JSON files. It won't be included in CSV exports as it's not required by platforms like OpenSea.
              </Typography>
            </Box>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Image Format" 
                secondary="Define the width and height of your NFTs (recommended: 1000x1000 pixels)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Image Smoothing" 
                secondary="Enable for better quality, disable for pixel art."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Collection Size" 
                secondary="Determine how many NFTs you want to generate (from 1 to thousands)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Rarity Delimiter" 
                secondary="Character used to separate the name and rarity in filenames (e.g., '#')."
              />
            </ListItem>
            <Box sx={{ ml: 4, my: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.08)', borderRadius: 2, border: '1px solid rgba(98, 0, 234, 0.2)' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                More about Rarity Delimiter
              </Typography>
              <Typography variant="body2" paragraph>
                The rarity delimiter is a special character that separates the trait name from its rarity weight in your image filenames. For example, in "blue_background#30.png", the "#" is the delimiter and "30" is the weight.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>How it works:</strong> The engine uses these weights to determine how frequently each trait appears. Higher weights mean more common traits. For example, if you have three backgrounds with weights 10, 30, and 60, they will appear with 10%, 30%, and 60% probability respectively.
              </Typography>
              <Typography variant="body2">
                <strong>Note:</strong> If you're uploading images one by one through the interface rather than in bulk, you don't need to include the delimiter in the filename. You can set the rarity directly in the interface after uploading each image.
              </Typography>
            </Box>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="DNA Uniqueness Tolerance" 
                secondary="Maximum number of retry attempts when a duplicate DNA is created."
              />
            </ListItem>
            <Box sx={{ ml: 4, my: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.08)', borderRadius: 2, border: '1px solid rgba(98, 0, 234, 0.2)' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                More about DNA Uniqueness Tolerance
              </Typography>
              <Typography variant="body2" paragraph>
                Each generated NFT has a unique "DNA" - a string that represents its specific combination of traits. The DNA uniqueness tolerance determines how many times the generator will attempt to create a new combination if it generates a duplicate.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Why it matters:</strong> A higher tolerance means the generator will try harder to avoid duplicates, but it may slow down the generation process. This is especially important for large collections with limited trait combinations.
              </Typography>
              <Typography variant="body2">
                <strong>Recommended settings:</strong> For small collections (under 100), a value of 10,000 works well. For larger collections, you might need to increase this value, especially if you have limited trait variations.
              </Typography>
            </Box>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(3, 218, 198, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon color="secondary" sx={{ mr: 1 }} /> Pro Tip
            </Typography>
            <Typography variant="body2">
              Choose your collection size carefully. While you can generate thousands of NFTs, starting with a smaller collection (50-100) is recommended for new creators to manage quality control.
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      label: 'Layer Setup',
      description: 'Create layers for your NFT traits.',
      icon: <LayersIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Layers are the building blocks of your NFT collection. Each layer represents a trait category (like background, body, eyes, etc.).
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Create Layers" 
                secondary="Add layers in the order they should be rendered (bottom to top)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Set Layer Names" 
                secondary="Name each layer descriptively (e.g., 'Background', 'Body', 'Eyes', 'Accessories')."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Configure Opacity" 
                secondary="Set the transparency level of each layer (100% means fully visible, lower values make the layer more transparent)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Layer Order" 
                secondary="Arrange layers in the correct stacking order using the up/down arrows."
              />
            </ListItem>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LightbulbIcon color="primary" sx={{ mr: 1 }} /> Important Concept
            </Typography>
            <Typography variant="body2">
              Layer order is crucial! Think of layers as transparent sheets stacked on top of each other. The bottom layer (usually the background) is drawn first, and each subsequent layer is drawn on top.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ mr: 1, color: '#ff9800' }} /> Common Mistake
            </Typography>
            <Typography variant="body2">
              Don't confuse layer opacity with trait rarity. Opacity controls how transparent a layer appears, while rarity (set when uploading images) determines how frequently a trait appears in your collection.
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      label: 'Image Upload and manage restrictions',
      description: 'Upload images for each layer, set up rarity and manage restrictions.',
      icon: <ImageIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            After setting up your layers, you'll need to upload the actual trait images for each layer.
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Prepare Your Images" 
                secondary="Create transparent PNG images for each trait. All images should have the same dimensions."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Upload to Layers" 
                secondary="Upload each trait image to its corresponding layer."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Set Trait Names" 
                secondary="Name each trait descriptively (e.g., 'Blue Background', 'Red Eyes')."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Configure Trait Rarity" 
                secondary="Set the rarity percentage for each trait within a layer."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Manage Trait Restrictions" 
                secondary="Control which traits can appear together by setting up restrictions. Click the gear icon next to any element to manage its restrictions."
              />
            </ListItem>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LightbulbIcon color="primary" sx={{ mr: 1 }} /> Two Ways to Set Trait Rarity
            </Typography>
            <Typography variant="body2" paragraph>
              1. Through the interface (recommended): When uploading images one by one, you can set the rarity directly in the interface after uploading each image. This is the easiest method and doesn't require any special naming convention.
            </Typography>
            <Typography variant="body2">
              2. Through filename (for bulk uploads): If you're preparing files for bulk upload, include the rarity delimiter and weight in the filename (e.g., "trait#30.png"). The number after the delimiter represents the relative weight - higher numbers mean more common traits.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(3, 218, 198, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon color="secondary" sx={{ mr: 1 }} /> About Restrictions
            </Typography>
            <Typography variant="body2" paragraph>
              You can control which traits can appear together by setting up restrictions. For example, you might want to prevent certain combinations from appearing in the same NFT.
            </Typography>
            <Typography variant="body2" paragraph>
              To set up restrictions, click on the gear icon <SettingsIcon sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> next to any element and select which other elements it should be incompatible with.
            </Typography>
            <Typography variant="body2" paragraph>
              This is useful for creating logical combinations, like preventing certain backgrounds from appearing with specific character types, or ensuring certain accessories are only available with specific body types.
            </Typography>
            <Typography variant="body2">
              <strong>Important:</strong> Restrictions work in both directions and are identical regardless of which element you set them from. For example, if you set that "Blue Background" is incompatible with "Alien", then "Alien" will also be incompatible with "Blue Background" - it's the exact same restriction. The generator will respect these restrictions regardless of which element is selected first.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ mr: 1, color: '#ff9800' }} /> Important Note
            </Typography>
            <Typography variant="body2">
              You can upload a transparent PNG image named "NONE.png" to represent the absence of a trait. When an image named "NONE" is selected during generation, that trait will not appear in the metadata. You can also apply rarity weights to NONE files using the rarity delimiter, for example: "NONE#90.png" for a 90% chance of the trait being absent.
            </Typography>
          </Box>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            <strong>Image Requirements:</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Format</Typography>
                <Typography variant="body2">PNG with transparency (recommended) or JPG</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Size</Typography>
                <Typography variant="body2">All images should have identical dimensions</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Naming</Typography>
                <Typography variant="body2">Use descriptive names without special characters (unless using the rarity delimiter)</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Recommended Resolution</Typography>
                <Typography variant="body2">1000x1000 pixels or higher for quality</Typography>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(3, 218, 198, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon color="secondary" sx={{ mr: 1 }} /> Pro Tip
            </Typography>
            <Typography variant="body2">
              When setting trait rarity, consider using the "weight" system. For example, if you want a trait to appear 5 times more frequently than another, give it a weight of 5 while the other has a weight of 1. Ideally, weights in each layer should sum to 100 for easy percentage calculation.
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      label: 'Unique NFTs (1/1)',
      description: 'Add special one-of-one NFTs to your collection.',
      icon: <DiamondIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            Unique NFTs (also called 1/1s) are special pieces in your collection that don't follow the layer-based generation. They are completely custom artworks that add exclusivity to your collection.
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Create Custom Artwork" 
                secondary="Design special pieces that stand out from your generated NFTs."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Upload Complete Images" 
                secondary="Upload your finished 1/1 artworks (not individual layers)."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Set Metadata" 
                secondary="Add custom names, descriptions, and attributes for each unique NFT."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Add Attributes" 
                secondary="Define specific attributes for each unique NFT to make them more special."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Choose NFT ID (Optional)" 
                secondary="Specify a custom ID number for your 1/1 NFT or let the system assign one randomly."
              />
            </ListItem>
            <Box sx={{ ml: 4, my: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.08)', borderRadius: 2, border: '1px solid rgba(98, 0, 234, 0.2)' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                About Custom NFT IDs
              </Typography>
              <Typography variant="body2" paragraph>
                When adding a unique 1/1 NFT to your collection, you can choose a specific ID number for it. This ID determines its position in the collection sequence.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>How it works:</strong> Enter your desired ID number in the "Custom ID" field when uploading your 1/1 NFT. For example, entering "88" will make this NFT appear as number 88 in your collection.
              </Typography>
              <Typography variant="body2">
                <strong>If left blank:</strong> The system will automatically assign a random ID to your 1/1 NFT during the generation process. This can be useful if you want your special pieces to be discovered randomly throughout the collection.
              </Typography>
            </Box>
          </List>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(98, 0, 234, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LightbulbIcon color="primary" sx={{ mr: 1 }} /> Strategy Tip
            </Typography>
            <Typography variant="body2">
              Unique 1/1 NFTs can significantly increase the appeal of your collection. Consider creating 5-10% of your collection as unique pieces to add rarity and exclusivity.
            </Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ mr: 1, color: '#ff9800' }} /> Important Note
            </Typography>
            <Typography variant="body2">
              This step is optional. If you don't want to add unique NFTs to your collection, you can skip directly to the collection generation step.
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      label: 'Collection Generation',
      description: 'Generate your unique NFT collection and download the results.',
      icon: <CodeIcon />,
      content: (
        <Box>
          <Typography variant="body1" paragraph>
            The final step is to generate your NFT collection. This process combines your layers according to their rarity settings, creates the images, and generates the metadata.
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Generate Collection" 
                secondary="Create all your NFT images and metadata files with one click."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Preview Results" 
                secondary="View a preview of your generated NFTs before downloading."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Download Files" 
                secondary="Get a ZIP file containing all your NFT images and metadata."
              />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            <strong>Output Files:</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Images</Typography>
                <Typography variant="body2">All your generated NFT images in your chosen format</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Metadata</Typography>
                <Typography variant="body2">JSON and CSV files with traits and attributes for each NFT</Typography>
              </Paper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(3, 218, 198, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon color="secondary" sx={{ mr: 1 }} /> Next Steps
            </Typography>
            <Typography variant="body2">
              After generating your collection, you'll typically want to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Upload images and metadata to IPFS for decentralized storage" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Deploy your NFT smart contract on your chosen blockchain" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="OR just set up and upload your collection (images and CSV metadata) on marketplaces like OpenSea" />
              </ListItem>
            </List>
          </Box>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper', 
          pt: 12,
          pb: 6,
          background: `linear-gradient(to right, ${theme.palette.background.default}, ${theme.palette.primary.dark})`,
          borderRadius: '0 0 20px 20px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(3, 218, 198, 0.1), transparent 70%)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            variant="outlined" 
            color="secondary"
            onClick={onBackToHome}
            sx={{ mb: 4, borderRadius: 2 }}
          >
            Back to Home
          </Button>
          <Typography
            component="h1"
            variant="h2"
            fontWeight="bold"
            sx={{
              background: `linear-gradient(90deg, #ffffff, ${theme.palette.secondary.light})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            NFT Creation Tutorial
          </Typography>
          <Typography variant="h5" color="white" paragraph>
            A comprehensive guide to creating your NFT collection using NFT Art Engine
          </Typography>
        </Container>
      </Box>

      {/* Tutorial Content */}
      <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Step-by-Step Guide
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          Follow this tutorial to learn how to create your own NFT collection from start to finish.
        </Typography>

        <Stepper orientation="vertical" sx={{ mt: 4 }}>
          {tutorialSteps.map((step, index) => (
            <Step key={step.label} active={true}>
              <StepLabel 
                StepIconComponent={() => (
                  <Box 
                    sx={{ 
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 2
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="h6" fontWeight="bold">
                  Step {index + 1}: {step.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 4 }}>
                  {step.content}
                </Box>
                <Divider sx={{ my: 2 }} />
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* Additional Resources */}
        <Paper elevation={3} sx={{ p: 4, mt: 6, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Additional Resources
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    NFT Best Practices
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Learn about the best practices for creating successful NFT collections.
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Quality over quantity" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Balanced trait distribution" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Create a compelling story" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Rarity Strategies
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Understand how to create effective rarity tiers in your collection.
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Common (50-70%)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Uncommon (15-30%)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Rare (5-15%)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Super Rare (1-5%)" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    OpenSea Compatibility
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Tips for ensuring your collection works perfectly on OpenSea.
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Follow metadata standards" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Use proper trait_type naming" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Include collection-level metadata" />
                    </ListItem>
                   
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Back to Home Button */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={onBackToHome}
            startIcon={<ArrowBackIcon />}
            sx={{ borderRadius: 2, px: 4, py: 1.5 }}
          >
            Return to Home Page
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TutorialPage; 